import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "@mui/material/styles";
import { Prediction, Game, BracketInfo } from "../../types/custom_types";
import Navbar from "./navbar";
import Footer from "./footer";
import Loading from "./Loading/Loading";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Bracket,
  IRoundProps,
  IRenderSeedProps,
  SingleLineSeed,
  SeedTeam,
  SeedItem,
  Seed,
} from "react-brackets";

const BracketDisplay = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [bracket, setBracket] = useState<BracketInfo>();
  const [games, setGames] = useState<Game[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [rounds, setRounds] = useState<IRoundProps[]>([]);
  const [didEliminations, setDidEliminations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allBrackets, setAllBrackets] = useState<BracketInfo[]>([]);

  const { id } = useParams();

  useEffect(() => {
    const fetchAllBrackets = async () => {
      const response = await axios.get<BracketInfo[]>(
        `${process.env.REACT_APP_API_URL}/userapi/brackets/`
      );
      setAllBrackets(response.data);
    };
    fetchAllBrackets();
  }, []);

  useEffect(() => {
    setLoading(true);
    setRounds([]);
    setPredictions([]);
    setGames([]);

    const fetchBracket = async () => {
      const response = await axios.get<BracketInfo>(
        `${process.env.REACT_APP_API_URL}/userapi/brackets/${id}/`
      );
      setBracket(response.data);
    };

    fetchBracket();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchGames = async () => {
      const response = await axios.get<Game[]>(
        `${process.env.REACT_APP_API_URL}/userapi/games/`
      );
      setGames(response.data);
    };

    fetchGames();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchPredictions = async () => {
      const response = await axios.get<Prediction[]>(
        `${process.env.REACT_APP_API_URL}/userapi/brackets/${id}/predictions/`
      );
      setPredictions(response.data);
    };

    fetchPredictions();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (predictions.length === 0 || games.length === 0) return;

    const gameMap = new Map<number, Game>();
    games.forEach((game) => gameMap.set(game.id, game));

    const predictionMap = new Map<number, Prediction>();
    predictions.forEach((prediction) =>
      predictionMap.set(prediction.game_id, prediction)
    );

    setPredictions((prev_predictions) => {
      prev_predictions.forEach((prediction) => {
        const game = gameMap.get(prediction.game_id);
        if (!game) return;

        if (game.winner_name) {
          prediction.team1_eliminated =
            game.winner_name !== prediction.predicted_team1_name;
          prediction.team2_eliminated =
            game.winner_name !== prediction.predicted_team2_name;
          return;
        }

        const previousPrediction1 = predictionMap.get(
          game.previous_game_for_team1_id as number
        );
        const previousPrediction2 = predictionMap.get(
          game.previous_game_for_team2_id as number
        );

        const team1WasTeam1 =
          previousPrediction1?.predicted_team1_name ===
          prediction.predicted_team1_name;

        prediction.team1_eliminated = previousPrediction1
          ? team1WasTeam1
            ? previousPrediction1.team1_eliminated
            : previousPrediction1?.team2_eliminated
          : false;

        const team2WasTeam1 =
          previousPrediction2?.predicted_team1_name ===
          prediction.predicted_team2_name;

        prediction.team2_eliminated = previousPrediction2
          ? team2WasTeam1
            ? previousPrediction2.team1_eliminated
            : previousPrediction2?.team2_eliminated
          : false;
      });
      return prev_predictions;
    });

    setDidEliminations(true);
  }, [predictions, games]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (games.length === 0 || predictions.length === 0 || !didEliminations)
      return;
    setDidEliminations(false);

    const gameMap = new Map<number, Game>();
    games.forEach((game) => gameMap.set(game.id, game));

    const calculateTeamScore = (
      predictedWinner: string,
      teamName: string,
      game: Game | undefined
    ): number => {
      if (!game || predictedWinner !== teamName) return 0;
      if (game.winner_name === teamName && game.round > 0)
        return 2 ** (game.round - 1);
      return 0;
    };

    const roundMap = new Map<number, Prediction[]>();
    predictions.forEach((prediction) => {
      const game = gameMap.get(prediction.game_id) as Game;
      if (game.round === 0) return;
      if (!roundMap.has(game.round)) roundMap.set(game.round, []);
      roundMap.get(game.round)?.push(prediction);
    });

    const roundTextGenerator = (round: number) => {
      if (round === 1) return "First Round";
      if (round === 2) return "Second Round";
      if (round === 3) return "Sweet 16";
      if (round === 4) return "Elite 8";
      if (round === 5) return "Final 4";
      if (round === 6) return "Championship";
      return `Round ${round}`;
    };

    const roundArr = Array.from(roundMap.entries()).map(
      ([round, prediction]) => ({
        title: roundTextGenerator(round),
        seeds: prediction.map((prediction) => {
          const game = gameMap.get(prediction.game_id);
          const team1Played =
            game?.team1_name === prediction.predicted_team1_name;
          const team2Played =
            game?.team2_name === prediction.predicted_team2_name;

          return {
            id: prediction.game_id,
            date: "",
            teams: [
              {
                id: `${prediction.predicted_team1_name}-${prediction.game_id}`,
                name: prediction.predicted_team1_name,
                score: calculateTeamScore(
                  prediction.predicted_winner_name,
                  prediction.predicted_team1_name,
                  game
                ),
                pickedAndWon:
                  game?.winner_name &&
                  prediction.predicted_winner_name ===
                    prediction.predicted_team1_name &&
                  game?.winner_name === prediction.predicted_team1_name,
                pickedAndLost:
                  game?.winner_name &&
                  team1Played &&
                  prediction.predicted_winner_name ===
                    prediction.predicted_team1_name &&
                  game?.winner_name !== prediction.predicted_team1_name,
                eliminated: prediction.team1_eliminated,
                chosen:
                  prediction.predicted_winner_name ===
                  prediction.predicted_team1_name,
                internalRound: game?.round,
                seed: game?.team1_seed,
                wins: game?.team1_wins,
                losses: game?.team1_losses,
              },
              {
                id: `${prediction.predicted_team2_name}-${prediction.game_id}`,
                name: prediction.predicted_team2_name,
                score: calculateTeamScore(
                  prediction.predicted_winner_name,
                  prediction.predicted_team2_name,
                  game
                ),
                pickedAndWon:
                  game?.winner_name &&
                  prediction.predicted_winner_name ===
                    prediction.predicted_team2_name &&
                  game?.winner_name === prediction.predicted_team2_name,
                pickedAndLost:
                  game?.winner_name &&
                  team2Played &&
                  prediction.predicted_winner_name ===
                    prediction.predicted_team2_name &&
                  game?.winner_name !== prediction.predicted_team2_name,
                eliminated: prediction.team2_eliminated,
                chosen:
                  prediction.predicted_winner_name ===
                  prediction.predicted_team2_name,
                internalRound: game?.round,
                seed: game?.team2_seed,
                wins: game?.team2_wins,
                losses: game?.team2_losses,
              },
            ],
          };
        }),
      })
    );

    setRounds(roundArr);
    setLoading(false);
  }, [predictions, games, didEliminations]); // eslint-disable-line react-hooks/exhaustive-deps

  const CustomSeed = ({
    seed,
    breakpoint,
    roundIndex,
    isMiddleOfTwoSided,
  }: IRenderSeedProps) => {
    console.log("starting roundIndex", roundIndex, "seed", seed);
    const Wrapper =
      (roundIndex === 4 || roundIndex === 0) && isMiddleOfTwoSided
        ? SingleLineSeed
        : Seed;
    return (
      <Wrapper mobileBreakpoint={breakpoint} style={{ fontSize: 12 }}>
        <SeedItem>
          <div>
            <SeedTeam
              style={{
                backgroundColor: seed.teams[0]?.pickedAndLost
                  ? "darkred"
                  : seed.teams[0]?.pickedAndWon
                  ? "darkgreen"
                  : seed.teams[0]?.eliminated
                  ? "grey"
                  : "",
                fontWeight: seed.teams[0]?.chosen ? "bold" : "normal",
              }}
            >
              {seed.teams[0]?.seed && seed.teams[0]?.internalRound === 1
                ? `${seed.teams[0]?.seed} `
                : ""}
              {seed.teams[0]?.name || "NO TEAM "}
              {seed.teams[0]?.score ? ` - ${seed.teams[0]?.score}` : ""}
            </SeedTeam>
            <SeedTeam
              style={{
                backgroundColor: seed.teams[1]?.pickedAndLost
                  ? "darkred"
                  : seed.teams[1]?.pickedAndWon
                  ? "darkgreen"
                  : seed.teams[1]?.eliminated
                  ? "grey"
                  : "",
                fontWeight: seed.teams[1]?.chosen ? "bold" : "normal",
              }}
            >
              {seed.teams[1]?.seed && seed.teams[1]?.internalRound === 1
                ? `${seed.teams[1]?.seed} `
                : ""}
              {seed.teams[1]?.name || "NO TEAM "}
              {seed.teams[1]?.score ? ` - ${seed.teams[1]?.score}` : ""}
            </SeedTeam>
          </div>
        </SeedItem>
      </Wrapper>
    );
  };

  const currentIndex = allBrackets.findIndex((b) => String(b.id) === String(id));
  const prevBracket = currentIndex > 0 ? allBrackets[currentIndex - 1] : null;
  const nextBracket = currentIndex < allBrackets.length - 1 ? allBrackets[currentIndex + 1] : null;

  if (loading) return <Loading />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: theme.gradients.contentBg,
      }}
    >
      <Navbar />

      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          mx: { xs: 1, md: 3 },
          mt: 3,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: theme.fonts.display,
              color: "primary.main",
              flex: 1,
            }}
          >
            {bracket?.full_name}
          </Typography>
          <Chip
            label={`${bracket?.score} pts`}
            color="secondary"
            sx={{ fontWeight: 700, fontSize: "1rem", px: 1 }}
          />
        </Box>

        {/* Rank navigation */}
        {allBrackets.length > 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              {prevBracket ? (
                <IconButton
                  size="small"
                  onClick={() => navigate(`/bracket/${prevBracket.id}`)}
                  sx={{ color: "primary.main" }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" disabled>
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              )}
              {prevBracket && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: { xs: 100, sm: 200 },
                  }}
                  onClick={() => navigate(`/bracket/${prevBracket.id}`)}
                >
                  #{currentIndex} {prevBracket.name}
                </Typography>
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600, whiteSpace: "nowrap", px: 1 }}
            >
              #{currentIndex + 1} of {allBrackets.length}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, justifyContent: "flex-end" }}>
              {nextBracket && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: { xs: 100, sm: 200 },
                    textAlign: "right",
                  }}
                  onClick={() => navigate(`/bracket/${nextBracket.id}`)}
                >
                  #{currentIndex + 2} {nextBracket.name}
                </Typography>
              )}
              {nextBracket ? (
                <IconButton
                  size="small"
                  onClick={() => navigate(`/bracket/${nextBracket.id}`)}
                  sx={{ color: "primary.main" }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" disabled>
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Bracket */}
      <Paper
        elevation={1}
        sx={{ mx: { xs: 1, md: 3 }, mt: 2, mb: 3, p: 2, overflowX: "auto" }}
      >
        <Bracket
          mobileBreakpoint={0}
          bracketClassName="custom-bracket"
          rounds={rounds}
          renderSeedComponent={CustomSeed}
          swipeableProps={{ enableMouseEvents: true, animateHeight: true }}
          twoSided={true}
          roundTitleComponent={(title: React.ReactNode) => (
            <div
              style={{ textAlign: "center", fontSize: "22px", color: theme.palette.primary.main, fontFamily: theme.fonts.display }}
            >
              {title}
            </div>
          )}
        />
      </Paper>

      <Footer />
    </Box>
  );
};

export default BracketDisplay;
